import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faArrowsLeftRight,
  faBell,
  faChartSimple,
  faChevronDown,
  faFilter,
  faGlobe,
  faHouse,
  faInfoCircle,
  faListUl,
  faTicketAlt,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { FiltersComponent } from '@shared/components/filters/filters.component';
import { HeaderComponent } from '@shared/components/header/header.component';
import { IcamInputComponent } from '@shared/components/icam-input/icam-input.component';
import { IcamSelectComponent } from '@shared/components/icam-select/icam-select.component';
import { IcamDatepickerComponent } from '@shared/components/icam-datepicker/icam-datepicker.component';
import { SidebarComponent } from '@shared/components/sidebar/sidebar.component';
import { ToastContainerComponent } from '@shared/components/toast-container/toast-container.component';
import { KnowledgeBaseBubbleComponent } from '@shared/components/knowledge-base-bubble/knowledge-base-bubble.component';
import { TableRowDetailsComponent } from '@shared/components/table-row-details/table-row-details.component';
import { TableRowComponent } from '@shared/components/table-row/table-row.component';
import { TableComponent } from '@shared/components/table/table.component';
import { LearningCardComponent } from '@shared/components/learning-card/learning-card.component';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { HomepageComponent } from './homepage/homepage.component';
import { LoginComponent } from './login/login.component';
import { RegisterComponent } from './register/register.component';

import { IcamContainerComponent } from '@shared/components/icam-container/icam-container.component';
import { IcamIconComponent } from '@shared/components/icam-icon/icam-icon.component';
import { IcamTicketComponent } from '@shared/components/icam-ticket/icam-ticket.component';
import { AccountComponent } from './account/account.component';
import { FinalUsersComponent } from './final-users/final-users.component';
import { MachineInformationsComponent } from './machine-informations/machine-informations.component';
import { MachinePerformancesComponent } from './machine-performances/machine-performances.component';
import { NewTicketComponent } from './new-ticket/new-ticket.component';
import { RegistrationCompleteComponent } from './registration-complete/registration-complete.component';
import { RequestComponent } from './request/request.component';
import { SentPasswordComponent } from './sent-password/sent-password.component';
import { TicketComponent } from './ticket/ticket.component';

import { NgbModule, NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { IcamChartComponent } from '@shared/components/icam-chart/icam-chart.component';
import { ProfileComponent } from '@shared/components/profile/profile.component';
import { NgChartsConfiguration, NgChartsModule } from 'ng2-charts';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { FinalUserDashboardComponent } from './final-user-dashboard/final-user-dashboard.component';
import { NewRequestComponent } from './new-request/new-request.component';

import { registerLocaleData } from '@angular/common';
import localeIt from '@angular/common/locales/it';
import { environment } from '@environments/environment';
import { NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { LanguageService } from '@shared/services/language.service';
import { LoggerModule } from 'ngx-logger';
import { ModalPersonalDataInfoComponent } from './register/modal-personal-data-info/modal-personal-data-info.component';
import { BackButtonComponent } from './shared/components/back-button/back-button.component';
import { BaseModalComponent } from './shared/components/base-modal/base-modal.component';
import { IcamMachineAlertComponent } from './shared/components/icam-machine-alert/icam-machine-alert.component';
import { IcamMachineWarrantyWarningComponent } from './shared/components/icam-machine-warranty-warning/icam-machine-warranty-warning.component';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { JwtInterceptor } from './_helpers/jwt.interceptor';
import { NewPasswordComponent } from './new-password/new-password.component';
import { HasRoleDirective } from './directives/has-role-client.directive';
import { StatusBadgeComponent } from './shared/components/status-badge/status-badge.component';
import { TermsAndConditionsComponent } from './shared/components/terms-and-conditions/terms-and-conditions.component';
import { PasswordValidatorsComponent } from './shared/components/password-validators/password-validators.component';
import { ChangePasswordComponent } from './change-password/change-password.component';
import { IcamMachineAlertWarningRecapComponent } from './shared/components/icam-machine-alert-warning-recap/icam-machine-alert-warning-recap.component';
import { AnalyticsWidgetComponent } from './shared/components/analytics-widget/analytics-widget.component';
import { AnalyticsCardComponent } from './shared/components/analytics-card/analytics-card.component';
import { AnalyticsPageComponent } from './analytics-page/analytics-page.component';
import { AnalyticsFilterComponent } from './shared/components/analytics-filter/analytics-filter.component';
import { AnalyticsPaginationControllerComponent } from './shared/components/analytics-pagination-controller/analytics-pagination-controller.component';
import { IcamDrawerComponent } from './shared/components/icam-drawer/icam-drawer.component';
import { AnalyticsChartComponent } from './shared/components/analytics-chart/analytics-chart.component';
import { IcamTabViewComponent } from './shared/components/icam-tab-view/icam-tab-view.component';
import { WarehouseStatusView } from '@shared/components/analytics-tabs/warehouse-status/warehouse-status-view.component';
import { MissionPerformanceView } from '@shared/components/analytics-tabs/missions-performance/mission-performance-view.component';
import { UserComponent } from './user/user.component';
import { UserFormComponent } from './user-form/user-form.component';
import { ModalConfirmUserStatusComponent } from './modal-confirm-user-status/modal-confirm-user-status.component';
import { IcamMultiSelectAutocomplete } from '@shared/components/icam-multi-select-autocomplete/icam-multi-select-autocomplete.component';
import { ClickOutsideDirective } from './directives/click-outside.directive';
import { SfContactDialogComponent } from './sf-contact-dialog/sf-contact-dialog.component';
import { CreateAdminModalComponent } from './create-admin-modal/create-admin-modal.component';
import { TooltipDirective } from './directives/tooltip.directive';
import { RepositoryComponent } from './repository/repository.component';
import { RequiredDocumentComponent } from './required-document/required-document.component';
import { RequiredDocumentFormComponent } from './required-document-form/required-document-form.component';
import { BannerComponent } from './banner/banner.component';
import { BannerFormComponent } from './banner-form/banner-form.component';
import { IcamAutocompleteSelectComponent } from '@shared/components/icam-autocomplete-select/icam-autocomplete-select.component';
import { BannerCarouselComponent } from './banner-carousel/banner-carousel.component';
import { UserWelcomeMessageFormComponent } from './user-welcome-message-form/user-welcome-message-form.component';
import { SalesOrderFormComponent } from './sales-order-form/sales-order-form.component';
import { SalesOrderDocumentsComponent } from './sales-order-documents/sales-order-documents.component';
import { LegalDocumentsComponent } from './legal-documents/legal-documents.component';
import { LegalDocumentFormComponent } from './legal-document-form/legal-document-form.component';
import { LegalDocumentsPreviewComponent } from './legal-documents-preview/legal-documents-preview.component';
import { IcamTabsComponent } from './shared/components/icam-tabs/icam-tabs.component';
import { IcamFileDownloaderComponent } from './shared/components/icam-file-downloader/icam-file-downloader.component';
import { OdvDocumentsFormComponent } from './odv-documents-form/odv-documents-form.component';
import { OdvHistoryComponent } from './shared/components/odv-history/odv-history.component';
import { LearningComponent } from './learning/learning.component';
import { LearningFormComponent } from './learning-form/learning-form.component';
import { AngularEditorModule } from '@kolkov/angular-editor';
import { FileInputComponent } from './file-input/file-input.component';
import { FileInputHistoryComponent } from './file-input-history/file-input-history.component';
import { FormSectionHeaderComponent } from './form-section-header/form-section-header.component';
import { LearningAttachmentInputComponent } from './learning-attachment-input/learning-attachment-input.component';
import { LearningDetailComponent } from './learning-detail/learning-detail.component';
import { LearningImageModalComponent } from './learning-image-modal/learning-image-modal.component';
import { RawHtmlComponent } from './shared/components/raw-html/raw-html.component';
import { LearningTagInputComponent } from './learning-tag-input/learning-tag-input.component';
import { VideoPlayerComponent } from './video-player/video-player.component';
import { LearningVideoModalComponent } from './learning-video-modal/learning-video-modal.component';
import { LearningFeedbackFormComponent } from './learning-feedback-form/learning-feedback-form.component';
import { CompanyGroupComponent } from './company-group/company-group.component';
import { CompanyGroupFormComponent } from './company-group-form/company-group-form.component';
import { CompanyGroupUsersComponent } from './company-group-users/company-group-users.component';
import { MachineModulesComponent } from './machine-modules/machine-modules.component';
import { MachineModulesFormComponent } from './machine-modules-form/machine-modules-form.component';
import { RatingInputComponent } from './rating-input/rating-input.component';
import { StorageAttachmentInputComponent } from './storage-attachment-input/storage-attachment-input.component';
import { CircularProgressComponent } from './circular-progress/circular-progress.component';
import { StorageInputHistoryComponent } from './storage-input-history/storage-input-history.component';
import { DividerComponent } from './divider/divider.component';
import { DiagnosticView } from '@shared/components/analytics-tabs/diagnostic-view/diagnostic-view.component';
import { MachineInventoryView } from '@shared/components/analytics-tabs/machine-inventory-view/machine-inventory-view.component';
import { DropdownButtonComponent } from './dropdown-button/dropdown-button.component';
import { AnalyticsExportButtonComponent } from './shared/components/analytics-export-button/analytics-export-button.component';
import { InfoPopoverComponent } from './info-popover/info-popover.component';
import { SmartEnergyView } from './shared/components/analytics-tabs/smart-energy-view/smart-energy-view.component';
import { FooterCopyrightComponent } from './footer-copyright/footer-copyright.component';
import { UserInfoSectionComponent } from './user-form/components/user-info-section/user-info-section.component';
import { AccountsSelectionComponent } from './user-form/components/accounts-selection/accounts-selection.component';
import { MachinesSelectionComponent } from './user-form/components/machines-selection/machines-selection.component';
import { PermissionsGridComponent } from './user-form/components/permissions-grid/permissions-grid.component';
import { CredentialsSectionComponent } from './user-form/components/credentials-section/credentials-section.component';

registerLocaleData(localeIt, 'it');

@NgModule({
  declarations: [
    AppComponent,
    RegisterComponent,
    HomepageComponent,
    SidebarComponent,
    HeaderComponent,
    ToastContainerComponent,
    KnowledgeBaseBubbleComponent,
    TableComponent,
    FiltersComponent,
    IcamSelectComponent,
    TableRowComponent,
    TableRowDetailsComponent,
    LearningCardComponent,
    MachineInformationsComponent,
    IcamContainerComponent,
    IcamTicketComponent,
    TicketComponent,
    RequestComponent,
    MachinePerformancesComponent,
    SentPasswordComponent,
    RegistrationCompleteComponent,
    AccountComponent,
    NewTicketComponent,
    FinalUsersComponent,
    IcamChartComponent,
    ProfileComponent,
    FinalUserDashboardComponent,
    NewRequestComponent,
    BackButtonComponent,
    ModalPersonalDataInfoComponent,
    BaseModalComponent,
    IcamMachineAlertComponent,
    IcamMachineWarrantyWarningComponent,
    NewPasswordComponent,
    HasRoleDirective,
    StatusBadgeComponent,
    TermsAndConditionsComponent,
    PasswordValidatorsComponent,
    ChangePasswordComponent,
    IcamMachineAlertWarningRecapComponent,
    AnalyticsPageComponent,
    AnalyticsWidgetComponent,
    AnalyticsCardComponent,
    AnalyticsFilterComponent,
    AnalyticsPaginationControllerComponent,
    IcamMultiSelectAutocomplete,
    ClickOutsideDirective,
    IcamDrawerComponent,
    AnalyticsChartComponent,
    IcamTabViewComponent,
    WarehouseStatusView,
    MissionPerformanceView,
    UserComponent,
    UserFormComponent,
    ModalConfirmUserStatusComponent,
    SfContactDialogComponent,
    CreateAdminModalComponent,
    TooltipDirective,
    RepositoryComponent,
    RequiredDocumentComponent,
    RequiredDocumentFormComponent,
    BannerComponent,
    BannerFormComponent,
    IcamAutocompleteSelectComponent,
    BannerCarouselComponent,
    UserWelcomeMessageFormComponent,
    SalesOrderFormComponent,
    SalesOrderDocumentsComponent,
    LegalDocumentsComponent,
    LegalDocumentFormComponent,
    LegalDocumentsPreviewComponent,
    IcamTabsComponent,
    IcamFileDownloaderComponent,
    OdvDocumentsFormComponent,
    OdvHistoryComponent,
    LearningComponent,
    LearningFormComponent,
    FileInputComponent,
    FileInputHistoryComponent,
    FormSectionHeaderComponent,
    LearningAttachmentInputComponent,
    LearningDetailComponent,
    LearningImageModalComponent,
    RawHtmlComponent,
    LearningTagInputComponent,
    VideoPlayerComponent,
    LearningVideoModalComponent,
    LearningFeedbackFormComponent,
    RatingInputComponent,
    StorageAttachmentInputComponent,
    CircularProgressComponent,
    StorageInputHistoryComponent,
    DividerComponent,
    DiagnosticView,
    MachineInventoryView,
    DropdownButtonComponent,
    AnalyticsExportButtonComponent,
    InfoPopoverComponent,
    SmartEnergyView,
    FooterCopyrightComponent,
    UserInfoSectionComponent,
    AccountsSelectionComponent,
    MachinesSelectionComponent,
    PermissionsGridComponent,
    CredentialsSectionComponent,
    CompanyGroupComponent,
    CompanyGroupFormComponent,
    CompanyGroupUsersComponent,
    MachineModulesComponent,
    MachineModulesFormComponent
  ],
  imports: [
    LoginComponent,
    ForgotPasswordComponent,
    IcamInputComponent,
    IcamIconComponent,
    IcamDatepickerComponent,
    FormsModule,
    ReactiveFormsModule,
    BrowserModule,
    HttpClientModule,
    FormsModule,
    AppRoutingModule,
    NgChartsModule,
    NgbDatepickerModule,
    NgbTypeaheadModule,
    NgxDaterangepickerMd.forRoot(),
    LanguageService.initTranslateModule(),
    LoggerModule.forRoot(environment.logs.logger),
    FontAwesomeModule,
    BrowserAnimationsModule,
    NgbModule,
    AngularFireModule.initializeApp(environment.firebaseConfig),
    AngularFireAuthModule,
    AngularEditorModule
  ],
  bootstrap: [AppComponent],
  providers: [
    NgChartsConfiguration,
    { provide: LOCALE_ID, useValue: 'it' },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true }
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule {
  constructor(library: FaIconLibrary) {
    library.addIcons(faHouse);
    library.addIcons(faTicketAlt);
    library.addIcons(faInfoCircle);
    library.addIcons(faGlobe);
    library.addIcons(faUser);
    library.addIcons(faBell);
    library.addIcons(faFilter);
    library.addIcons(faArrowsLeftRight);
    library.addIcons(faListUl);
    library.addIcons(faChartSimple);
    library.addIcons(faChevronDown);
  }
}
